import { useCallback, useEffect, useRef, useState } from "react";
import { api, TIMEOUTS } from "@/lib/api";

/**
 * Dictarea, transcrisă pe serverul nostru.
 *
 * `SpeechRecognition` din browser trimite sunetul la Google sau Microsoft.
 * Adică exact INTRAREA — unde se rostesc cifre, nume de clienți și sume — pleacă
 * în afară, în timp ce IEȘIREA (sinteza) stă acasă. Aici microfonul se
 * înregistrează local și octeții merg la `/api/dictate`, care are whisper copt
 * în imagine.
 *
 * Măsurat pe 3 secunde de sunet: 2,8 s cu modelul cald. Nu e conversație — e
 * „vorbești, apoi apare".
 */
const MIME = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];

export function canRecord() {
  return typeof navigator !== "undefined"
    && Boolean(navigator.mediaDevices?.getUserMedia)
    && typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined";
}

function pickMime() {
  if (typeof window === "undefined" || !window.MediaRecorder?.isTypeSupported) return "";
  return MIME.find((t) => window.MediaRecorder.isTypeSupported(t)) || "";
}

export default function useDictation({ workspaceId, onText }) {
  const [recording, setRecording] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const onTextRef = useRef(onText);
  useEffect(() => { onTextRef.current = onText; }, [onText]);

  // Microfonul nu rămâne deschis nici dacă panoul dispare în mijlocul frazei.
  const release = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);
  useEffect(() => release, [release]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* deja oprit */ }
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    if (!canRecord()) { setError("Browserul nu poate înregistra."); return; }
    setError("");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Microfonul nu a pornit: permisiunea a fost refuzată.");
      return;
    }
    streamRef.current = stream;
    const type = pickMime();
    const rec = new window.MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    rec.onstop = async () => {
      release();
      const blob = new Blob(chunks, { type: type || "audio/webm" });
      if (!blob.size) return;
      setWorking(true);
      try {
        const form = new FormData();
        form.append("file", blob, "dictare.webm");
        const { data } = await api.post("/dictate", form, {
          params: { workspace_id: workspaceId },
          headers: { "Content-Type": "multipart/form-data" },
          // Termenul acoperă urcarea, coada și transcrierea; fără el, cele 15
          // secunde implicite ar tăia o dictare care așteaptă în coadă.
          timeout: TIMEOUTS.dictate,
        });
        if (data?.text) onTextRef.current?.(data.text);
        else setError("N-am auzit nimic.");
      } catch (e) {
        setError(e?.response?.data?.detail || "Transcrierea nu a reușit.");
      } finally {
        setWorking(false);
      }
    };
    recRef.current = rec;
    rec.start();
    setRecording(true);
  }, [release, workspaceId]);

  return {
    supported: canRecord(), recording, working, error,
    start, stop, toggle: () => (recording ? stop() : start()),
  };
}
